import { Component, OnInit } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { ActivatedRoute, Router } from '@angular/router';
import { SettingsService } from '../../../services/settings.service';
import { UserService } from '../../../services/user.service';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { SpinnerModule } from '@coreui/angular';
import { IconModule, IconSetService } from '@coreui/icons-angular';
import { iconSubset } from '../../../icons/icon-subset';

@Component({
  selector: 'app-user-form',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, SpinnerModule, IconModule],
  templateUrl: './user-form.component.html',
  styleUrls: ['./user-form.component.scss']
})
export class UserFormComponent implements OnInit {
  userForm: FormGroup;
  loading = false;
  settingsRaw: any[] = [];
  userTypeOptions: any[] = [];
    showPassword = false;

    constructor(
      private fb: FormBuilder,
      private userService: UserService,
      private settingsService: SettingsService,
      private iconSet: IconSetService,
      private route: ActivatedRoute,
      private router: Router,
      private toastr: ToastrService
    ) {
      this.userForm = this.fb.group({
        first_name: ['', [Validators.required, Validators.maxLength(255)]],
        last_name: ['', [Validators.required, Validators.maxLength(255)]],
        email: ['', [Validators.required, Validators.email]],
        user_type: ['', [Validators.required, Validators.maxLength(50)]],
        password: ['', [Validators.required, Validators.minLength(6)]],
        password_confirmation: ['', [Validators.required, Validators.minLength(6)]],
        status: [true] // true = active, false = deactivated
      });
      this.userForm.disable();
      this.iconSet.icons = { ...iconSubset };
    }

  userId: string | null = null;

  // Toggle user status (active/deactivated)
  toggleStatus(): void {
    const current = this.userForm.get('status')?.value;
    this.userForm.get('status')?.setValue(!current);
  }

    togglePasswordVisibility(): void {
      this.showPassword = !this.showPassword;
    }

    copyPassword(): void {
      const password = this.userForm.get('password')?.value || '';
      if (navigator.clipboard) {
        navigator.clipboard.writeText(password);
      } else {
        // fallback for older browsers
        const textarea = document.createElement('textarea');
        textarea.value = password;
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }
    }

    ngOnInit(): void {
      this.route.paramMap.subscribe(params => {
        this.userId = params.get('id');
        if (this.userId) {
          // Edit mode: fetch user and patch form
          this.loading = true;
          this.userService.getUserById(this.userId).subscribe({
            next: (user) => {
              this.userForm.patchValue({
                first_name: user.first_name,
                last_name: user.last_name,
                email: user.email,
                user_type: user.user_type,
                status: user.status === 'active' || user.status === true,
                password: '',
                password_confirmation: ''
              });
              this.loading = false;
              this.userForm.enable();
            },
            error: () => {
              this.loading = false;
              this.userForm.enable();
            }
          });
        } else {
          this.userForm.enable();
        }
      });
      this.settingsService.getSettings().subscribe({
        next: (settings) => {
          this.settingsRaw = Array.isArray(settings) ? settings : (settings.data || []);
          this.userTypeOptions = this.settingsRaw.filter(s => s.code === 'user_type');
        }
      });
      }
  
    get isFormValid(): boolean {
      if (this.loading) return false;
      const v = this.userForm.value;
      if (this.userId) {
        // Edit mode: only require fillable fields
        return !!v.first_name && !!v.last_name && !!v.email && !!v.user_type && (v.status !== undefined && v.status !== null);
      } else {
        // Add mode: require all fields including password and confirmation
        return !!v.first_name && !!v.last_name && !!v.email && !!v.user_type && !!v.password && !!v.password_confirmation && (v.status !== undefined && v.status !== null);
      }
    }

    suggestPassword(): void {
      const chars = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*()';
      let password = '';
      for (let i = 0; i < 12; i++) {
        password += chars.charAt(Math.floor(Math.random() * chars.length));
      }
      this.userForm.get('password')?.setValue(password);
      this.userForm.get('password_confirmation')?.setValue(password);
    }

    onSubmit(): void {
      if (this.isFormValid) {
        this.loading = true;
        if (this.userId) {
          // Edit mode: update user
          const payload = { ...this.userForm.value, status: this.userForm.get('status')?.value ? 'active' : 'deactivated' };
          this.userService.updateUser(this.userId, payload).subscribe({
            next: (res) => {
              this.loading = false;
              this.toastr.success('User updated successfully!', 'Success', {
                closeButton: true,
                progressBar: true,
                timeOut: 3000
              });
              this.router.navigate(['/users']);
            },
            error: (err) => {
              this.loading = false;
              this.toastr.error(err?.error?.message || 'Failed to update user', 'Error', {
                closeButton: true,
                progressBar: true,
                timeOut: 5000
              });
            }
          });
        } else {
          // Add mode: create user
          const payload = { ...this.userForm.value, status: this.userForm.get('status')?.value ? 'active' : 'deactivated' };
          this.userService.createUser(payload).subscribe({
            next: (res) => {
              this.loading = false;
              this.toastr.success('User added successfully!', 'Success', {
                closeButton: true,
                progressBar: true,
                timeOut: 3000
              });
              this.router.navigate(['/users']);
            },
            error: (err) => {
              this.loading = false;
              this.toastr.error(err?.error?.message || 'Failed to add user', 'Error', {
                closeButton: true,
                progressBar: true,
                timeOut: 5000
              });
            }
          });
        }
      }
    }
  }
